// AuthCallback handles the redirect from Emergent Google OAuth.
// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";

export default function AuthCallback() {
    const hasProcessed = useRef(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if (hasProcessed.current) return;

        const hash = window.location.hash || "";
        const match = hash.match(/session_id=([^&]+)/);
        if (!match) {
            window.location.replace("/login");
            return;
        }

        // Guard against StrictMode double-mount: cache session_id in sessionStorage
        const sessionId = decodeURIComponent(match[1]);
        const lastProcessed = sessionStorage.getItem("sf_last_session_id");
        if (lastProcessed === sessionId) {
            // already exchanged — just redirect home
            hasProcessed.current = true;
            window.location.replace("/app");
            return;
        }

        hasProcessed.current = true;
        sessionStorage.setItem("sf_last_session_id", sessionId);

        (async () => {
            try {
                await api.post("/auth/google/session", { session_id: sessionId });
                // Hard redirect so AuthProvider re-runs /me with the new cookie
                window.location.replace("/app");
            } catch (e) {
                console.error("Google auth exchange failed", e);
                sessionStorage.removeItem("sf_last_session_id");
                const status = e.response?.status;
                const detail = e.response?.data?.detail;
                const baseMsg = formatApiError(detail);
                const diag = status
                    ? `HTTP ${status}${typeof detail === "string" ? ` · ${detail}` : ""}`
                    : e.message || "Errore di rete";
                setError(
                    baseMsg && baseMsg !== "Si è verificato un errore. Riprova."
                        ? `${baseMsg} (${diag})`
                        : `Impossibile completare l'accesso. ${diag}`
                );
            }
        })();
    }, []);

    if (error) {
        return (
            <div
                className="min-h-screen flex items-center justify-center bg-sf-bg p-6"
                data-testid="auth-callback-error"
            >
                <div className="max-w-sm w-full bg-white border border-sf rounded-2xl p-8 text-center">
                    <div className="font-display text-2xl text-navy mb-2">
                        Accesso non riuscito
                    </div>
                    <div className="text-sf-secondary text-sm mb-6">{error}</div>
                    <Link
                        to="/login"
                        className="inline-block bg-navy text-white px-5 py-2.5 rounded-md font-semibold"
                        data-testid="auth-callback-retry"
                    >
                        Torna al login
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div
            className="min-h-screen flex items-center justify-center bg-sf-bg"
            data-testid="auth-callback"
        >
            <div className="text-center">
                <div className="font-display text-3xl text-navy animate-pulse">
                    Accesso in corso…
                </div>
                <div className="text-sf-muted text-sm mt-2">
                    Stiamo collegando il tuo account Google.
                </div>
            </div>
        </div>
    );
}
